import { createFileRoute } from "@tanstack/react-router";

import { SiteFooter } from "@/components/site-footer";
import { HomeNav } from "@/components/home-nav";
import { ExhibitionVideos } from "@/components/exhibition-videos";
import { portraits } from "@/data/about";
import { ProjectCarousel } from "@/components/project-carousel";
import cvEx1 from "@/assets/cv-exhibition-1.webp.asset.json";
import cvEx2 from "@/assets/cv-exhibition-2.webp.asset.json";
import cvEx3 from "@/assets/cv-exhibition-3.webp.asset.json";
import cvEx4 from "@/assets/cv-exhibition-4.webp.asset.json";
import cvEx5 from "@/assets/cv-exhibition-5.webp.asset.json";
import cvEx6 from "@/assets/cv-exhibition-6.webp.asset.json";
import cvEx7 from "@/assets/cv-exhibition-7.webp.asset.json";
import cvEx8 from "@/assets/cv-exhibition-8.webp.asset.json";
import cvEx9 from "@/assets/cv-exhibition-9.webp.asset.json";
import cvEx10 from "@/assets/cv-exhibition-10.jpeg.asset.json";
import cvEx11 from "@/assets/cv-exhibition-11.webp.asset.json";
import cvEx12 from "@/assets/cv-exhibition-12.webp.asset.json";
import creativeLeadershipCv from "@/assets/creative-leadership-cv.pdf.asset.json";
import { cvDownload, cvSections, cvUpdated } from "@/data/cv";

export const Route = createFileRoute("/cv")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/cv" },
      { title: "Curriculum — Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Curriculum of Luiza Jesus do Prado: academic formation, certifications, biennials, residencies, public collections, solo and collective exhibitions, performances, awards and lectures.",
      },
      { property: "og:title", content: "Curriculum — Luiza Jesus do Prado" },
      {
        property: "og:description",
        content:
          "Academic formation, biennials, residencies, public collections, exhibitions, performances and awards.",
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: portraits[0]?.src ?? "" },
      { name: "twitter:image", content: portraits[0]?.src ?? "" },
    ],
  }),
  component: Cv,
});

function Cv() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      <main id="main-content" className="px-4 pb-24 pt-32 md:px-[6vw] md:pt-40">
        {/* Hero — oversized word bleeding to the right */}
        <section className="relative overflow-hidden">
          <h1 className="font-display text-[clamp(1.8rem,4vw,3.2rem)] font-light leading-[1] text-bone">
            Curriculum
          </h1>

          <div className="mt-6 grid grid-cols-1 gap-3 border-t border-bone/15 pt-4 font-sans text-[11px] font-light uppercase tracking-[0.16em] text-bone/70 md:grid-cols-12">
            <span className="md:col-span-3">{cvUpdated}</span>
            <span className="md:col-span-3">Based worldwide</span>
            <a
              href={cvDownload}
              target="_blank"
              rel="noreferrer"
              className="text-bone transition-opacity hover:opacity-60 md:col-span-3"
            >
              Download full CV (PDF) ↗
            </a>
            <a
              href={creativeLeadershipCv.url}
              download="Luiza-Prado-Creative-Leadership-CV.pdf"
              className="text-bone transition-opacity hover:opacity-60 md:col-span-3"
            >
              Creative Leadership CV (PDF) ↓
            </a>
          </div>
        </section>

        {/* Sections */}
        <div className="mt-16 md:mt-24">
          {cvSections.map((section) => (
            <section
              key={section.no}
              className="grid grid-cols-1 gap-4 border-t border-bone/15 py-8 md:grid-cols-12 md:gap-6 md:py-10"
            >
              <div className="md:col-span-4">
                <p className="font-sans text-[10px] font-light uppercase tracking-[0.2em] text-bone/65">
                  ({section.no})
                </p>
                <h2 className="mt-2 font-display text-[clamp(1.05rem,1.8vw,1.5rem)] font-light leading-[1.05] text-bone">
                  {section.title}
                </h2>
              </div>

              <ul className="md:col-span-8">
                {section.entries.map((entry, i) => (
                  <li
                    key={`${section.no}-${i}`}
                    className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-4 border-b border-bone/10 py-3 last:border-b-0"
                  >
                    <span className="shrink-0 font-sans text-[11px] font-light tracking-[0.12em] text-bone/65">
                      {entry.year ?? "—"}
                    </span>
                    <span className="min-w-0 font-sans text-[12px] font-light leading-[1.55] text-bone/85 md:text-[13px]">
                      {entry.url ? (
                        <a
                          href={entry.url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline decoration-bone/30 underline-offset-4 transition-opacity hover:opacity-60"
                        >
                          {entry.text} ↗
                        </a>
                      ) : (
                        entry.text
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="border-t border-bone/15 pt-8">
          <ProjectCarousel
            images={[cvEx1,cvEx2,cvEx3,cvEx4,cvEx5,cvEx6,cvEx7,cvEx8,cvEx9,cvEx10,cvEx11,cvEx12].map((a, i) => ({ src: a.url, alt: `Exhibition record ${i + 1}` }))}
          />
        </section>

        <section className="mt-8 border-t border-bone/15 pt-8">
          <ExhibitionVideos />
        </section>

      </main>

      <SiteFooter />
    </div>
  );
}
